import React, { useEffect, useMemo, useRef, useState } from "react";

/** Simple word bank (all caps). Add more if you like. */
const WORDS = [
  "MOUNTAIN","OCEAN","FOREST","DESERT","ISLAND","RIVER","THUNDER","LIGHTNING",
  "PENGUIN","ELEPHANT","KANGAROO","FLAMINGO","DOLPHIN","TIGER","PANTHER",
  "PUZZLE","CROSSWORD","RIDDLE","ENIGMA","MYSTERY","ADVENTURE","DISCOVERY",
  "CALIFORNIA","ORANGE","DANA POINT","NEIGHBORHOOD","COMMUNITY","LOCAL", 
  "banana","oranges","grapefruit","pineapple","strawberry","blueberry","cucumber","pumpkin","lettuce","spinach",
  "elephant","giraffe","dolphin","kangaroo","penguin","chicken","turkey","donkey","parrots","rabbit","teacher",
  "student","holiday","weekend","morning","evening","bedroom","kitchen","library","village","garden","airport",
  "station","country","mountain","valleys","deserts","oceanic",
  "islands",
  "rainbow",
  "sunrise",
  "sunset",
  "flowers",
  "weather",
  "thunder",
  "lightning",
  "rainfall",
  "snowman",
  "iceberg",
  "friendship",
  "brothers",
  "sisters",
  "parents",
  "children",
  "happiness",
  "laughter",
  "musical",
  "dancing",
  "painting",
  "reading",
  "writing",
  "journey",
  "travels",
  "adventure",
  "freedom",
  "kindness",
  "courage",
  "respect",
  "honesty",
  "village",
  "country",
  "citizen",
  "capital",
  "festival",
  "market",
  "station",
  "harvest",
  "seasonal",
  "picnics",
  "playground","USA","CANADA","MEXICO","BRAZIL","ARGENTINA","CHILE","PERU",
  "COLOMBIA","VENEZUELA","ECUADOR","BOLIVIA","PARAGUAY","URUGUAY","GUYANA",
  "SURINAME","FRENCH GUIANA","CUBA","JAMAICA","HAITI","DOMINICAN REPUBLIC",
  "PUERTO RICO","TRINIDAD AND TOBAGO","BARBADOS","BAHAMAS","SAINT LUCIA",
  "GRENADA","ANTIGUA AND BARBUDA","SAINT VINCENT AND THE GRENADINES",
  "DOMINICA","SAINT KITTS AND NEVIS","BELIZE","HONDURAS","EL SALVADOR",
  "GUATEMALA","NICARAGUA","PANAMA","COSTA RICA","JAPAN","CHINA","INDIA",
];

const ABC = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const MAX_WRONG = 6; // head, body, L arm, R arm, L leg, R leg

export default function HangmanGame() {
  const [secret, setSecret] = useState(pickWord());
  const [guessed, setGuessed] = useState(new Set());
  const wrong = useMemo(
    () => ABC.filter(ch => guessed.has(ch) && !secret.includes(ch)).length,
    [guessed, secret]
  );
  const playing = wrong < MAX_WRONG && !isSolved(secret, guessed);
  const status = playing ? "playing" : isSolved(secret, guessed) ? "won" : "lost";

  const inputRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      const k = e.key.toUpperCase();
      if (/^[A-Z]$/.test(k) && playing) {
        e.preventDefault();
        onGuess(k);
      } else if (e.key === "Enter" && !playing) {
        newGame();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing, secret, guessed]);

  function onGuess(letter) {
    if (guessed.has(letter)) return;
    setGuessed(prev => new Set(prev).add(letter));
  }

  function newGame() {
    setSecret(pickWord());
    setGuessed(new Set());
    // focus first keyboard button if rendered
    inputRef.current?.focus();
  }

  const masked = mask(secret, guessed);

  return (
    <div className="hm-wrap">
      <style>{CSS}</style>

      <div className="hm-card">
        <div className="hm-header">
          <h1 className="hm-title">Hangman</h1>
          <div className="hm-sub">Guess the word, one letter at a time.</div>
        </div>

        <div className="hm-grid">
          {/* Left: Gallows / figure */}
          <div className="hm-gallows">
            <HangmanDrawing wrong={wrong} />
            <div className="hm-counter">
              Mistakes: <strong>{wrong}</strong>/<span>{MAX_WRONG}</span>
            </div>
          </div>

          {/* Right: Word + keyboard */}
          <div className="hm-panel">
            <div
              className={`hm-word ${status === "won" ? "won" : ""} ${
                status === "lost" ? "lost" : ""
              }`}
              aria-live="polite"
            >
              {masked.split("").map((ch, i) => (
                <span className={`hm-slot ${ch === " " ? "space" : ""}`} key={i}>
                  {ch === " " ? "\u00A0" : ch}
                </span>
              ))}
            </div>

            {status === "playing" ? (
              <div className="hm-keys">
                {ABC.map((ch, i) => {
                  const used = guessed.has(ch);
                  const miss = used && !secret.includes(ch);
                  return (
                    <button
                      key={ch}
                      ref={i === 0 ? inputRef : null}
                      className={`hm-key ${used ? "used" : ""} ${
                        miss ? "miss" : ""
                      }`}
                      onClick={() => onGuess(ch)}
                      disabled={used}
                      aria-label={`Guess ${ch}`}
                    >
                      {ch}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={`hm-result ${status}`}>
                {status === "won" ? (
                  <span>🎉 You got it!</span>
                ) : (
                  <span>💀 Game over. The word was <b>{formatSecret(secret)}</b>.</span>
                )}
              </div>
            )}

            <div className="hm-actions">
              <button className="hm-btn" onClick={newGame}>
                {status === "playing" ? "New Word" : "Play Again"}
              </button>
              <button
                className="hm-btn ghost"
                onClick={() => revealOne(setGuessed, secret)}
                disabled={!playing}
                title="Reveal a random letter"
              >
                Hint
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function pickWord() {
  const w = WORDS[(Math.random() * WORDS.length) | 0];
  return w.toUpperCase().replace(/-/g, " "); // hyphens shown as spaces
}

function isSolved(secret, guessed) {
  for (const ch of secret) {
    if (/[A-Z]/.test(ch) && !guessed.has(ch)) return false;
  }
  return true;
}

function mask(secret, guessed) {
  let out = "";
  for (const ch of secret) {
    out += /[A-Z]/.test(ch) ? (guessed.has(ch) ? ch : "•") : " ";
  }
  return out;
}

function revealOne(setGuessed, secret) {
  const needed = [...new Set(secret.split("").filter(c => /[A-Z]/.test(c)))];
  const already = new Set();
  setGuessed(prev => {
    prev.forEach(v => already.add(v));
    const missing = needed.filter(ch => !already.has(ch));
    if (missing.length) {
      const pick = missing[(Math.random() * missing.length) | 0];
      const next = new Set(prev);
      next.add(pick);
      return next;
    }
    return prev;
  });
}

function formatSecret(s) {
  // show with spaces between letters for readability
  return s.split("").join(" ").replace(/\s{2,}/g, " ");
}

/* ---------- SVG drawing ---------- */
function HangmanDrawing({ wrong }) {
  // gallows is static; parts appear with mistakes 1..6
  return (
    <svg viewBox="0 0 140 160" className="hm-svg" role="img" aria-label="Hangman drawing">
      {/* gallows */}
      <line x1="10" y1="150" x2="130" y2="150" className="gal" />
      <line x1="40" y1="150" x2="40" y2="20" className="gal" />
      <line x1="40" y1="20" x2="95" y2="20" className="gal" />
      <line x1="95" y1="20" x2="95" y2="35" className="gal" />
      {/* parts */}
      {wrong > 0 && <circle cx="95" cy="50" r="15" className="part" />}            {/* head */}
      {wrong > 1 && <line x1="95" y1="65" x2="95" y2="105" className="part" />}     {/* body */}
      {wrong > 2 && <line x1="95" y1="75" x2="78" y2="90" className="part" />}      {/* L arm */}
      {wrong > 3 && <line x1="95" y1="75" x2="112" y2="90" className="part" />}     {/* R arm */}
      {wrong > 4 && <line x1="95" y1="105" x2="80" y2="130" className="part" />}    {/* L leg */}
      {wrong > 5 && <line x1="95" y1="105" x2="110" y2="130" className="part" />}   {/* R leg */}
    </svg>
  );
}

/* ---------- styles (no framework required) ---------- */
const CSS = `
.hm-wrap{min-height:100vh;background:#f6f7fb;padding:24px;display:flex;justify-content:center}
.hm-card{max-width:980px;width:100%;background:#fff;border:1px solid #e5e7eb;border-radius:16px;box-shadow:0 1px 2px rgba(0,0,0,.05)}
.hm-header{padding:18px 20px;border-bottom:1px solid #f0f2f5}
.hm-title{margin:0;font-size:28px;line-height:1.2;font-weight:800;color:#0f172a}
.hm-sub{margin-top:4px;color:#64748b;font-size:14px}
.hm-grid{display:grid;grid-template-columns:280px 1fr;gap:12px;padding:16px}
@media (max-width:760px){.hm-grid{grid-template-columns:1fr}}

.hm-gallows{display:flex;flex-direction:column;align-items:center;gap:6px;padding:8px}
.hm-svg{width:100%;max-width:260px;height:auto}
.gal{stroke:#334155;stroke-width:6;stroke-linecap:round}
.part{stroke:#ef4444;stroke-width:6;stroke-linecap:round;fill:none}

.hm-counter{font-size:14px;color:#475569}
.hm-panel{padding:8px}

.hm-word{display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:center;background:#f8fafc;border:1px dashed #e5e7eb;border-radius:12px;padding:12px 14px;margin-bottom:12px}
.hm-word.won{background:#ecfdf5;border-color:#34d399}
.hm-word.lost{background:#fff1f2;border-color:#fda4af}
.hm-slot{min-width:24px;border-bottom:3px solid #94a3b8;font-weight:700;font-size:24px;letter-spacing:1px;text-align:center;color:#0f172a}
.hm-slot.space{border-bottom:0;width:16px}

.hm-keys{display:grid;grid-template-columns:repeat(13,minmax(0,1fr));gap:6px}
@media (max-width:760px){.hm-keys{grid-template-columns:repeat(9,minmax(0,1fr))}}
.hm-key{height:38px;border:1px solid #cbd5e1;background:#fff;border-radius:10px;font-weight:700;color:#0f172a;cursor:pointer}
.hm-key:hover{background:#f1f5f9}
.hm-key.used{opacity:.7;cursor:default}
.hm-key.miss{background:#fee2e2;border-color:#fecaca}

.hm-actions{display:flex;gap:8px;margin-top:12px}
.hm-btn{height:40px;padding:0 14px;border-radius:10px;background:#0f172a;color:#fff;border:0;cursor:pointer;font-weight:700}
.hm-btn:hover{opacity:.95}
.hm-btn.ghost{background:#eef2f7;color:#0f172a}

.hm-result{display:flex;justify-content:center;align-items:center;height:42px;font-weight:700;border-radius:10px}
.hm-result.won{background:#ecfdf5;color:#065f46;border:2px solid #34d399}
.hm-result.lost{background:#fff1f2;color:#9f1239;border:2px solid #fda4af}
`;